import { Controller } from "@hotwired/stimulus"

// Shared background-filter controller for wizard previews. Listens for
// change/input events on the live-preview fields that
// Common::BackgroundPickerComponent emits — overlay_darkness, tint,
// tint_opacity, blur_px, saturate — and applies them to the preview's
// bg image + scrim + tint layers.
//
// This decouples filter handling from per-template preview controllers.
// New templates get filter support for free by:
//   1. Mounting the controller on the wizard shell (set
//      @stimulus_controller = "<template>-live-preview background-filter"
//      in the WizardContainerComponent)
//   2. Adding three data-target hooks to the preview HTML:
//        data-background-filter-target="bgImage"      — the bg image div
//        data-background-filter-target="overlayScrim" — the dark scrim layer
//        data-background-filter-target="tintScrim"    — the tint color layer
//      (Each is optional — the controller no-ops what's missing.)
//
// If the bg picker isn't currently rendered in DOM (e.g. the user is on
// a wizard step that doesn't include the picker), the controller
// leaves the preview's static defaults in place — the no-input guard
// in apply* methods ensures it never overrides a static CSS scrim
// with 0-darkness rgba.
export default class extends Controller {
  static targets = ["bgImage", "overlayScrim", "tintScrim"]

  connect() {
    this.handler = () => this.applyAll()
    this.fields().forEach((el) => {
      el.addEventListener("input", this.handler)
      el.addEventListener("change", this.handler)
    })
    this.applyAll()
  }

  disconnect() {
    if (!this.handler) return
    this.fields().forEach((el) => {
      el.removeEventListener("input", this.handler)
      el.removeEventListener("change", this.handler)
    })
  }

  fields() {
    return this.element.querySelectorAll(
      '[data-live-preview-field="overlay_darkness"],' +
        '[data-live-preview-field="tint"],' +
        '[data-live-preview-field="tint_opacity"],' +
        '[data-live-preview-field="blur_px"],' +
        '[data-live-preview-field="saturate"]'
    )
  }

  fieldElement(name) {
    return this.element.querySelector(`[data-live-preview-field="${name}"]`)
  }

  fieldNumber(name, fallback = null) {
    const el = this.fieldElement(name)
    if (!el) return fallback
    const n = parseFloat(el.value)
    return Number.isFinite(n) ? n : fallback
  }

  applyAll() {
    this.applyImageFilters()
    this.applyOverlay()
    this.applyTint()
  }

  applyImageFilters() {
    if (!this.hasBgImageTarget) return
    // No-input guard: if neither blur nor saturate fields are
    // rendered, don't touch the bgImage's filter style.
    if (!this.fieldElement("blur_px") && !this.fieldElement("saturate")) return
    const blur = this.fieldNumber("blur_px", 0)
    const sat = this.fieldNumber("saturate", 1)
    this.bgImageTarget.style.filter = `blur(${blur}px) saturate(${sat})`
  }

  applyOverlay() {
    if (!this.hasOverlayScrimTarget) return
    // No-input guard: only override the static CSS scrim when the
    // overlay_darkness field is in DOM (i.e. user is on the step that
    // surfaces the bg picker).
    const el = this.fieldElement("overlay_darkness")
    if (!el) return
    const darkness = parseFloat(el.value)
    if (!Number.isFinite(darkness)) return
    this.overlayScrimTarget.style.backgroundColor = `rgba(0,0,0,${darkness})`
  }

  applyTint() {
    if (!this.hasTintScrimTarget) return
    const colorEl = this.fieldElement("tint")
    const opacityEl = this.fieldElement("tint_opacity")
    // No-input guard: if neither field is in DOM, don't touch the tint
    // layer (preview keeps its static state).
    if (!colorEl && !opacityEl) return
    const color = colorEl?.value || ""
    const opacity = parseFloat(opacityEl?.value || "0")
    if (color && opacity > 0) {
      this.tintScrimTarget.style.backgroundColor = this.hexToRgba(color, opacity)
      this.tintScrimTarget.style.display = "block"
    } else {
      this.tintScrimTarget.style.display = "none"
    }
  }

  hexToRgba(hex, alpha) {
    const c = (hex || "").replace("#", "")
    if (c.length < 6) return `rgba(0,0,0,${alpha})`
    const r = parseInt(c.substring(0, 2), 16)
    const g = parseInt(c.substring(2, 4), 16)
    const b = parseInt(c.substring(4, 6), 16)
    return `rgba(${r},${g},${b},${alpha})`
  }
}
